import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Cols from 'components/Layout/Cols';
import Select from 'components/Inputs/Select';
import TextInput from 'components/Inputs/TextInput';
import { STEPS, LIST_OF_FIELDS as allFieldTypes } from '../config';
import FieldType from './type';
import Section from '../../../section';
import { useFlashTransition } from 'hooks/useFlashState';
import styled from '@emotion/styled';
import { DESCRIPTION_VISIBILITY, FIELD_TYPES } from 'utility/constants';
import LongText from 'components/Inputs/LongText';
import { useSelector } from 'react-redux';
import { getBusinessTeamMembersObject } from 'store/authentication/selectors';

const exceptions = [
  'category',
  'name',
  'displayName',
  'description',
  'descriptionVisibility',
  'type',
];
const StyledTextAreaInput = styled(LongText)`
  > div {
    height: auto;
  }
`;
const FieldSettings = ({
  updateField,
  resetField,
  updateValidation,
  formData,
  categories,
  data,
  errors,
}) => {
  const { t } = useTranslation();
  const teamMembersObject = useSelector(getBusinessTeamMembersObject);
  const isTeamMembers = data.model?.id === teamMembersObject.id;

  const isEdit = !!data.field;
  const isValid = Boolean(
    formData.displayName.trim() &&
      formData.category &&
      (!isEdit || formData.name.trim())
  );

  // custom objects has RelationshipOptions
  const fieldTypes = allFieldTypes.filter(
    (f) => !isTeamMembers || f.type !== FIELD_TYPES.Relationship.type // is not supported yet for teammember fields
  );

  useEffect(() => {
    updateValidation(STEPS.FIELD_SETTINGS, isValid);
  }, [isValid, updateValidation]);

  const [error, showError, flashError] = useFlashTransition();
  useEffect(() => {
    if (errors?.name || errors?.displayName || errors?.description) {
      flashError(errors);
    }
  }, [errors, flashError]);

  const options = DESCRIPTION_VISIBILITY.map(({ value, label }) => ({
    value: value,
    label: label(t),
  }));

  return (
    <>
      <Section index={0} header={t('Field Settings')} isActive>
        <Cols columns={isTeamMembers ? 1 : 2} gutter="20px">
          <TextInput
            label={t('Field Name')}
            value={formData.displayName}
            placeholder={t('Enter Field Name')}
            onChange={(value) => updateField('displayName', value)}
            validate={{
              message: error?.displayName,
              showMessage: error?.displayName && showError,
            }}
            margin={true}
          />
          {isTeamMembers ? null : (
            <Select
              fullWidth
              label={t('Category')}
              options={
                Array.isArray(categories)
                  ? categories.map((cat) => ({
                      value: cat.id,
                      label: cat.name,
                    }))
                  : categories
              }
              value={formData.category}
              onChange={(value) => updateField('category', value)}
              margin={true}
            />
          )}
        </Cols>
        {isEdit ? (
          <Cols columns={2} gutter="20px">
            <TextInput
              label={t('Field API Name')}
              value={formData.name}
              placeholder={t('Enter Field API Name')}
              onChange={(value) =>
                updateField('name', (value || '').toLowerCase())
              }
              labelInfo={t(
                'Warning: Changing the API Name will break integrations using this name, as well as email merge fields referencing this field name. Only letters, numbers, and _ are allowed.'
              )}
              validate={{
                message: error?.name,
                showMessage: error?.name && showError,
              }}
              margin={true}
              error={errors?.name}
            />
            <TextInput
              label={t('Field ID')}
              value={data.field.id}
              disabled
              margin={true}
            />
          </Cols>
        ) : null}
        <Cols columns={2} gutter="20px">
          <StyledTextAreaInput
            label={t('Description')}
            enableResize={false}
            value={formData.description}
            placeholder={t('Enter Description')}
            onChange={(value) => updateField('description', value)}
            validate={{
              message: error?.description,
              showMessage: error?.description && showError,
            }}
            error={errors?.description}
            margin={true}
            fullHeight={true}
            rows={5}
          />
          <Select
            fullWidth
            label={t('Description Visibility')}
            options={options}
            value={formData.descriptionVisibility}
            onChange={(value) => updateField('descriptionVisibility', value)}
            margin={true}
          />
        </Cols>
      </Section>
      <FieldType
        formData={formData}
        updateField={updateField}
        resetField={resetField}
        updateValidation={updateValidation}
        index={1}
        isActive
        data={data}
        fieldTypes={fieldTypes}
        exceptions={exceptions}
      />
    </>
  );
};

export default FieldSettings;
